import React, { Component } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Paper, withStyles } from "@material-ui/core";
import {
  FilteringState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SortingState,
  SummaryState,
  IntegratedSummary,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  PagingPanel,
  Table,
  TableFilterRow,
  TableBandHeader,
  TableHeaderRow,
} from "@devexpress/dx-react-grid-material-ui";

const styles = {
  customCell: {
    whiteSpace: "normal",
    verticalAlign: "top"
  },
};

const CustomTableCellBase = ({ classes, ...restProps }) => (
  <Table.Cell className={classes.customCell} {...restProps} />
);

export const CustomTableCell = withStyles(styles)(CustomTableCellBase);

const propsCellStyles = {
  customCell: {
    whiteSpace: "normal",
    verticalAlign: "baseline",
    border: "1px solid #f1f1f1",
  },
};

const CustomPropsCellBase = ({ classes, ...restProps }) => (
  <Table.Cell className={classes.customCell} {...restProps} />
);

export const CustomPropsCell = withStyles(propsCellStyles)(CustomPropsCellBase);

const propsCellStylesVerticalTop = {
  customCell: {
    whiteSpace: "normal",
    verticalAlign: "middle",
    border: "1px solid #f1f1f1",
  },
};

const CustomVerticalTopCellBase = ({ classes, ...restProps }) => (
  <Table.Cell className={classes.customCell} {...restProps} />
);

export const CustomVerticalTopCell = withStyles(propsCellStylesVerticalTop)(CustomVerticalTopCellBase);

class DxGridTable extends Component {
  EmptyDataCell = (props) => {
    return this.props.isLoading ? (
      <td colSpan={props.colSpan}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 50,
            width: "100%",
            overflow: "hidden",
            height: "90px",
          }}
        >
          <CircularProgress />
        </div>
      </td>
    ) : (
      <Table.NoDataCell {...props} />
    );
  };

  render() {
    const {
      tableColumnExtensions,
      defaultSorting,
      sortingStateColumnExtensions,
      filteringStateColumnExtensions,
      defaultFilters,
      pageSizes,
      data,
      columns,
      showFilter,
      hidePagination,
      columnBands,
      isColumnBands,
      isBorderCellStyleVerticalTop,
      hasActionColumn,
    } = this.props;

    // Only include extensions for columns that actually exist
    const columnNames = columns.map(col => col.name);
    
    const finalSortingExtensions = sortingStateColumnExtensions.filter(
      ext => columnNames.includes(ext.columnName)
    );
    
    const finalFilteringExtensions = filteringStateColumnExtensions.filter(
      ext => columnNames.includes(ext.columnName)
    );

    const BandCellBase = ({ children, column, ...restProps }) => {
      return (
        <TableBandHeader.Cell {...restProps} column={column}>
          <strong>{children}</strong>
        </TableBandHeader.Cell>
      );
    };

    return (
      <Paper style={this.props.mainTablePaperStyle}>
        <Grid rows={data} columns={columns}>
          
          <FilteringState
            defaultFilters={defaultFilters}
            columnExtensions={finalFilteringExtensions}
          />
          <IntegratedFiltering />
          <SortingState
            defaultSorting={defaultSorting}
            columnExtensions={finalSortingExtensions}
          />
          <IntegratedSorting />

          <PagingState defaultPageSize={10} />
          {!hidePagination && <IntegratedPaging />}
          <Table
            columnExtensions={tableColumnExtensions}
            noDataCellComponent={this.EmptyDataCell}
            cellComponent={
              this.props.isBorderCellStyle ? isBorderCellStyleVerticalTop ?  CustomVerticalTopCell : CustomPropsCell : CustomTableCell
            }
          />
          <TableHeaderRow
            showSortingControls={true}
            titleComponent={(props) =>
              hasActionColumn && props.children === "Action" ? (
                <b>&emsp;{props.children}</b>
              ) : (
                <b>{props.children}</b>
              )
            }
          />
          {isColumnBands && (
            <TableBandHeader
              columnBands={columnBands}
              cellComponent={BandCellBase}
            />
          )}
          <SummaryState totalItems={this.props.summaryItems} />
          <IntegratedSummary />
          {showFilter ? <TableFilterRow showFilterSelector={true} /> : ""}
          {!hidePagination && <PagingPanel pageSizes={pageSizes} />}
        </Grid>
      </Paper>
    );
  }
}

DxGridTable.propTypes = {
  mainTablePaperStyle: PropTypes.object,
  summaryItems: PropTypes.array,
  data: PropTypes.array,
  columnBands: PropTypes.array,
  isColumnBands: PropTypes.bool,
  columns: PropTypes.array,
  showFilter: PropTypes.bool,
  hidePagination: PropTypes.bool,
  formatColumns: PropTypes.array,
  currencyColumns: PropTypes.array,
  availableFilterOperations: PropTypes.array,
  pageSizes: PropTypes.array,
  defaultSorting: PropTypes.array,
  sortingStateColumnExtensions: PropTypes.array,
  tableColumnExtensions: PropTypes.array,
  resizingMode: PropTypes.string,
  defaultFilters: PropTypes.array,
  filteringStateColumnExtensions: PropTypes.array,
  isLoading: PropTypes.bool,
  hideInsideTableLoading: PropTypes.bool,
  classes: PropTypes.string,
  isBorderCellStyle: PropTypes.bool,
  isBorderCellStyleVerticalTop: PropTypes.bool,
  hasActionColumn: PropTypes.bool,
};

DxGridTable.defaultProps = {
  data: [],
  columnBands: [],
  isColumnBands: false,
  summaryItems: [],
  columns: [],
  mainTablePaperStyle: {},
  showFilter: false,
  hidePagination: false,
  formatColumns: [],
  currencyColumns: [],
  availableFilterOperations: [
    "equal",
    "notEqual",
    "greaterThan",
    "greaterThanOrEqual",
    "lessThan",
    "lessThanOrEqual",
  ],
  isBorderCellStyle: false,
  isBorderCellStyleVerticalTop: false,
  pageSizes: [5, 10, 15, 20],
  defaultSorting: [],
  sortingStateColumnExtensions: [
  ],
  tableColumnExtensions: [],
  defaultColumnWidths: [],
  resizingMode: "widget",
  defaultFilters: [],
  filteringStateColumnExtensions: [
  ],
  isLoading: false,
  classes: [],
  hasActionColumn: false,
};

export default DxGridTable;